/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import ErrorName from '../error/error-name';
import { errorWithLevelWarning } from '../error/error-with-level';
import { toISOString } from '../iso-string';
import OrientationEnum, { isHorizontal } from '../orientation/orientation-enum';

/**
 * @import { DriveInfo, FileSystemUnitInfo, ReportInfo } from '.'
 * @import { FileSystemManager } from '../file/system'
 * @import { FirmwareManager } from '../firmware'
 * @import ScreenManager from '../screen/screen-manager'
 * @import SensorManager from '../sensor/sensor-manager'
 * @import { ContentInfo } from '../content/info/content-info'
 * @import { Logger } from '../logger/logger'
 * @import PowerManager from '../power/power-manager'
 * @import { ViewerRegistry } from '../viewer/registry/'
 * @import { Session } from '../session/session'
 */

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 * @typedef {Record<string, *>} ControlManager TODO #555
 * @typedef {Record<string, *>} MemoryManager TODO #555
 * @typedef {Record<string, *>} NetworkManager TODO #555
 * @typedef {Record<string, *>} SoundManager TODO #555
 * @typedef {Record<string, *>} TimeManager TODO #555
 */

/**
 * @typedef {Pick<Session, 'storage'>} ISession
 */

/**
 * @typedef {Object} IWindow
 * @property {number} [devicePixelRatio]
 * @property {{width: number, height: number}} screen
 */

// TODO Rozšířit i o infomace přidané v DeviceStateful#getReportInfo
// - Bude možné až po migraci všech platforem na multiplatformní Adapter.
// - Přidá položky: buildId, contentTimestamp, version.
/**
 * Správce informací pro reportování.
 */
export default class ReportManager {
  /**
   * Vytvoří instanci.
   * @param {AppManager} appManager Správce aplikace.
   * @param {string | null} buildId Identifikátor sestavení.
   * @param {ControlManager} controlManager Správce ovládání.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {FirmwareManager} firmwareManager Správce firmware.
   * @param {Logger} logger
   * @param {MemoryManager} memoryManager Správce operační paměti.
   * @param {NetworkManager} networkManager Správce připojení k síti.
   * @param {PowerManager} powerManager Správce napájení.
   * @param {ScreenManager} screenManager Správce obrazovky.
   * @param {SensorManager} sensorManager Správce fyzických senzorů.
   * @param {ISession} session Sezení s nastavením zařízení.
   * @param {SoundManager} soundManager Správce zvuku.
   * @param {TimeManager} timeManager Správce času a časových zón.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @param {IWindow} [windowObj = window] Globální objekt okna pro přístup k obrazovce.
   */
  constructor(
    appManager,
    buildId,
    controlManager,
    fileSystemManager,
    firmwareManager,
    logger,
    memoryManager,
    networkManager,
    powerManager,
    screenManager,
    sensorManager,
    session,
    soundManager,
    timeManager,
    viewerRegistry,
    windowObj = window
  ) {
    this.appManager = appManager;
    this.buildId = buildId;
    this.controlManager = controlManager;
    this.fileSystemManager = fileSystemManager;
    this.firmwareManager = firmwareManager;
    this.logger = logger;
    this.memoryManager = memoryManager;
    this.networkManager = networkManager;
    this.powerManager = powerManager;
    this.screenManager = screenManager;
    this.sensorManager = sensorManager;
    this.session = session;
    this.soundManager = soundManager;
    this.timeManager = timeManager;
    this.viewerRegistry = viewerRegistry;
    this.window = windowObj;
  }

  /**
   * Zjistí část informací s časem zapnutí zařízení.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'bootTimestamp'>>} Informace.
   */
  async getBootTimestamp() {
    return {
      bootTimestamp: toISOString(await this.powerManager.getBootDate())
    };
  }

  /**
   * Zjistí část informací o staženém obsahu na zařízení.
   * @param {Pick<ContentInfo, 'media'> | null} contentInfo
   * @private
   * @returns {Promise<Pick<ReportInfo, 'content'>>} Informace.
   */
  async getContent(contentInfo) {
    if (isNil(contentInfo)) {
      return {};
    }
    const mediaPendingAsset = contentInfo.media.filter(({ type, asset }) => {
      const viewerType = this.viewerRegistry.find(type);
      const isOfflineable = viewerType && viewerType.isOfflineable();
      return isNil(asset) && isOfflineable;
    });

    return await {
      content: {
        asset: { pending: mediaPendingAsset.length, media: mediaPendingAsset }
      }
    };
  }

  /**
   * @private
   * @param {Pick<ContentInfo, 'contentTimestamp'> | null} contentInfo
   * @returns {Promise<Pick<ReportInfo, 'contentTimestamp'>>}
   */
  async getContentTimestamp(contentInfo) {
    return {
      contentTimestamp: (await contentInfo?.contentTimestamp) ?? undefined
    };
  }

  /**
   * Zjistí část informací s podsvícením obrazovky.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'backlight'>>} Informace.
   */
  async getBacklight() {
    return await withNotSupportedErrorHandling(async () => ({
      backlight: await this.screenManager.getBacklight()
    }));
  }

  /**
   * Zjistí část informací s jasem obrazovky.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'brightness'>>} Informace.
   */
  async getBrightness() {
    return await withNotSupportedErrorHandling(async () => ({
      brightness: await this.screenManager.getBrightness()
    }));
  }

  /**
   * Zjistí identifikační klíč dané platformy.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'customKey'>>} Informace.
   */
  async getCustomKey() {
    return { customKey: await this.appManager.getCustomKey() };
  }

  /**
   * Získá seznam custom položek z běžící platformy.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'customReport'>>} Informace.
   */
  async getCustomReport() {
    return { customReport: await this.appManager.getCustomReport() };
  }

  /**
   * @private
   * @param {Pick<ContentInfo, 'deviceName'> | null} contentInfo
   * @returns {Promise<Pick<ReportInfo, 'deviceName'>>} Informace.
   */
  async getDeviceName(contentInfo) {
    return { deviceName: (await contentInfo?.deviceName) ?? undefined };
  }

  /**
   * Zjistí část informací s fyzickými datovými úložišťi.
   * @param {Pick<ContentInfo, 'communicatorSettings'> | null} contentInfo
   * @private
   * @returns {Promise<Pick<ReportInfo, 'driveInfo'>>} Informace.
   */
  async getDriveInfo(contentInfo) {
    const units = await this.fileSystemManager.getAllUnits();
    /** @type {FileSystemUnitInfo[]} */
    const fileSystemUnitInfoArray = await Promise.all(
      units.map(async (unit) => {
        const id = unit.getName();
        const type = unit.getType();
        const info = await unit.getInfo();
        const { free, total, used } = info;
        return { id, type, free, total, used };
      })
    );
    // TODO: DRY, Device, ContentDownloader, ReportManager
    const type =
      contentInfo?.communicatorSettings.get('storage.types') ??
      this.session.storage;
    return {
      /** @type {DriveInfo} TODO #555 */
      driveInfo: { fileSystemUnitInfoArray, type }
    };
  }

  /**
   * Zjistí část informací o firmware:
   * - manufacturer,
   * - modelName,
   * - version
   * @private
   * @returns {Promise<Pick<ReportInfo, 'manufacturer' | 'modelName' | 'firmwareVersion'>>} Informace.
   */
  async getFirmwareInfo() {
    const { manufacturer, modelName, version } =
      await this.firmwareManager.getInfo();
    return { manufacturer, modelName, firmwareVersion: version };
  }

  /**
   * Zjistí část informací ohledně skutečně nastavených prázdnin.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'holidays'>>} Informace.
   */
  async getHolidays() {
    return {
      holidays: await this.powerManager.getHolidays()
    };
  }

  /**
   * Zjistí část informací se snímkem obrazovky.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'image'>>} Informace.
   */
  async getImage() {
    try {
      return { image: await this.screenManager.createScreenshot() };
    } catch (e) {
      throw errorWithLevelWarning(/** @type {Error} */ (e));
    }
  }

  /**
   * Zjistí informace pro reportování.
   * @param {Pick<ContentInfo,
   *   | 'communicatorSettings'
   *   | 'contentTimestamp'
   *   | 'deviceName'
   *   | 'media'
   * > | null} contentInfo
   * @returns {Promise<ReportInfo>} Informace.
   */
  async getInfo(contentInfo) {
    return await [
      () => this.getBootTimestamp(),
      () => this.getBuildId(),
      () => this.getContent(contentInfo),
      () => this.getContentTimestamp(contentInfo),
      () => this.getBacklight(),
      () => this.getBrightness(),
      () => this.getCustomKey(),
      () => this.getCustomReport(),
      () => this.getDeviceName(contentInfo),
      () => this.getDriveInfo(contentInfo),
      () => this.getFirmwareInfo(),
      () => this.getHolidays(),
      () => this.getImage(),
      () => this.getLocalControlLock(),
      () => this.getMemory(),
      () => this.getMuted(),
      () => this.getNetwork(),
      () => this.getNowTimestamp(),
      () => this.getOrientation(),
      () => this.getResolution(),
      () => this.getRemoteControlLock(),
      () => this.getRuntimePlatform(),
      () => this.getSerialNumber(),
      () => this.getScaleFactor(),
      () => this.getTemperature(),
      () => this.getTimezone(),
      () => this.getUpdatePlatform(),
      () => this.getUpdateUrl(),
      () => this.getVolume()
    ].reduce(async (accPromise, getter) => {
      const accObject = await accPromise;
      try {
        const result = await getter();
        Object.assign(accObject, result);
      } catch (e) {
        const error = /** @type {Error} */ (e);
        // Chyby vycházející z nedostupnosti informací se ignorují.
        if (error.name !== ErrorName.NotSupportedError) {
          this.logger.error(error);
        }
      }
      return accObject;
    }, Promise.resolve({}));
  }

  /**
   * @private
   * @returns {Promise<Pick<ReportInfo, 'buildId'>>}
   */
  async getBuildId() {
    return { buildId: (await this.buildId) ?? undefined };
  }

  /**
   * Zjistí část informací se zámkem ovládání pro tlačítka umístěné
   * přímo na zařízení.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'localControlLock'>>}
   */
  async getLocalControlLock() {
    return { localControlLock: await this.controlManager.getLocalLock() };
  }

  /**
   * Zjistí část informací s operační pamětí.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'totalRam' | 'usedRam'>>} Informace.
   */
  async getMemory() {
    const { total, used } = await this.memoryManager.getInfo();
    return { totalRam: total, usedRam: used };
  }

  /**
   * Zjistí část informací s vypnutím zvuku.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'muted'>>} Informace.
   */
  async getMuted() {
    return { muted: await this.soundManager.isMuted() };
  }

  /**
   * Zjistí část informací s připojením k síti.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'networkConnections'>>} Informace.
   */
  async getNetwork() {
    return {
      networkConnections: await this.networkManager.getAllConnections()
    };
  }

  /**
   * Zjistí část informací s aktuálním časem.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'nowTimestamp'>>} Informace.
   */
  async getNowTimestamp() {
    return { nowTimestamp: await toISOString(new Date()) };
  }

  /**
   * Zjistí část informací s orientací obrazovky.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'orientationEnum'>>} Informace.
   */
  async getOrientation() {
    const orientationEnum = await this.screenManager.getOrientation();
    if (orientationEnum === OrientationEnum.UNKNOWN) {
      return {};
    }
    return { orientationEnum };
  }

  /**
   * Zjistí část informací se zámkem ovládání pro tlačítka na dálkovém ovládání.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'remoteControlLock'>>}
   */
  async getRemoteControlLock() {
    return { remoteControlLock: await this.controlManager.getRemoteLock() };
  }

  /**
   * Zjistí část informací s rozlišením obrazovky.
   * @private
   * @returns {Promise<ReportInfo>}
   */
  async getResolution() {
    const { width, height } = this.window.screen;
    const orientation = await this.screenManager.getOrientation();
    // TODO: Vymýtit UNKNOWN.
    if (orientation === OrientationEnum.UNKNOWN) {
      return {};
    }
    const resolution =
      !isHorizontal(orientation) && width >= height
        ? { width: height, height: width }
        : { width, height };

    return { resolution };
  }

  /**
   * Zjistí identifikátor platformy, na které ja aplikace momentálně spuštěná.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'runtimePlatform'>>} Informace.
   */
  async getRuntimePlatform() {
    return { runtimePlatform: await this.firmwareManager.getRuntimePlatform() };
  }

  /**
   * Zjistí část informací se sériovým číslem zařízení.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'serialNumber'>>} Informace.
   */
  async getSerialNumber() {
    return { serialNumber: await this.firmwareManager.getSerialNumber() };
  }

  /**
   * Zjistí část informací se scale factorem (DPI).
   * @private
   * @returns {Promise<Pick<ReportInfo, 'scaleFactor'>>}
   */
  async getScaleFactor() {
    const scaleFactor = this.window.devicePixelRatio;
    return await { scaleFactor };
  }

  /**
   * Zjistí část informací s teplotou zařízení.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'temperature'>>} Informace.
   */
  async getTemperature() {
    return { temperature: await this.sensorManager.getTemperature() };
  }

  /**
   * Zjistí část informací s časovou zónou.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'timezone'>>} Informace.
   */
  async getTimezone() {
    return { timezone: await this.timeManager.getZone() };
  }

  /**
   * Zjistí část informací s platformou pro aktualizace.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'updatePlatform'>>} Informace.
   */
  async getUpdatePlatform() {
    return { updatePlatform: await this.appManager.getUpdatePlatform() };
  }

  /**
   * Zjistí část informací s aktualizační URL.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'updateUrl'>>} Informace.
   */
  async getUpdateUrl() {
    return { updateUrl: await this.appManager.getUpdateUrl() };
  }

  /**
   * Zjistí část informací s hlasitostí zvuku.
   * @private
   * @returns {Promise<Pick<ReportInfo, 'volume'>>} Informace.
   */
  async getVolume() {
    return { volume: await this.soundManager.getVolume() };
  }
}

/**
 * Zaobalí volání operace do bloku try/catch a chyby NotSupportedError
 * přepošle jako varování.
 * @template T
 * @param {() => Promise<T>} getter
 * @returns {Promise<T>}
 */
async function withNotSupportedErrorHandling(getter) {
  try {
    return await getter();
  } catch (e) {
    const error = /** @type {Error} */ (e);
    if (error.name === ErrorName.NotSupportedError) {
      throw errorWithLevelWarning(error);
    }
    throw error;
  }
}
